import api from './api';
import API_ENDPOINTS from '../config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SliderColorVariant = 'mint' | 'pink' | 'yellow' | 'purple';

export interface SliderItem {
  id: number;
  title: string;
  subtitle: string | null;
  button_label: string;
  button_url: string;
  image_url: string | null;
  color_variant: SliderColorVariant;
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSliderPayload {
  title: string;
  subtitle?: string;
  button_label: string;
  button_url: string;
  image_url?: string | null;
  color_variant?: SliderColorVariant;
  order?: number;
  is_active?: boolean;
}

export interface UpdateSliderPayload extends Partial<CreateSliderPayload> {}

export interface ReorderPayload {
  items: { id: number; order: number }[];
}

// ─── Public API (no auth required) ───────────────────────────────────────────

export const slidersService = {
  /** Fetch active slides for the public homepage */
  getPublicSliders: async (): Promise<SliderItem[]> => {
    const response = await api.get<SliderItem[]>(
      API_ENDPOINTS.SLIDERS.replace(API_ENDPOINTS.BASE_URL, ''),
    );
    return response.data;
  },

  // ─── Admin API (requires auth + admin role) ────────────────────────────────

  /** Fetch ALL slides (including inactive) for the admin dashboard */
  getAdminSliders: async (): Promise<SliderItem[]> => {
    const response = await api.get<SliderItem[]>(
      API_ENDPOINTS.ADMIN_SLIDERS.replace(API_ENDPOINTS.BASE_URL, ''),
    );
    return response.data;
  },

  uploadImage: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post<{ url: string }>(
      `${API_ENDPOINTS.ADMIN_SLIDERS.replace(API_ENDPOINTS.BASE_URL, '')}/upload-image`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },

  createSlider: async (payload: CreateSliderPayload): Promise<SliderItem> => {
    const response = await api.post<SliderItem>(
      API_ENDPOINTS.ADMIN_SLIDERS.replace(API_ENDPOINTS.BASE_URL, ''),
      payload,
    );
    return response.data;
  },

  updateSlider: async (
    id: number,
    payload: UpdateSliderPayload,
  ): Promise<SliderItem> => {
    const response = await api.patch<SliderItem>(
      `${API_ENDPOINTS.ADMIN_SLIDERS.replace(API_ENDPOINTS.BASE_URL, '')}/${id}`,
      payload,
    );
    return response.data;
  },

  deleteSlider: async (id: number): Promise<void> => {
    await api.delete(
      `${API_ENDPOINTS.ADMIN_SLIDERS.replace(API_ENDPOINTS.BASE_URL, '')}/${id}`,
    );
  },

  reorderSliders: async (payload: ReorderPayload): Promise<SliderItem[]> => {
    const response = await api.patch<SliderItem[]>(
      `${API_ENDPOINTS.ADMIN_SLIDERS.replace(API_ENDPOINTS.BASE_URL, '')}/reorder`,
      payload,
    );
    return response.data;
  },
};

export default slidersService;
